import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import { Box, Button, CircularProgress, Container, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import CustomToolTip from "../components/CustomToolTip";
import RegionPicker from '../components/RegionPicker';
import useSnackbar from "../components/snackbar/useSnackBar";
import TranslationTextFields from '../components/TranslationTextFields';
import { useApi } from '../hooks/useApi';
import { useLanguage } from '../hooks/useLanguage';
import { hasLanguageSet, REGION_KEYS, UNIT_CHOICES } from "../utils/lib";
import AttributeBaseSelection from './AttributeBaseSelection';
import AttributeRegionValues from './AttributeRegionValues';

/**
 * Edit attribute.
 * @param {*} param0 
 * @returns 
 */
export default function EditAttribute({ userInfo, id, onClose }) {

  const [data, setData] = useState({
    key: '',
    localization: {
      de: '',
      en: '',
    }, type: 'TEXT', publicFlag: 'false',
    defaults: {},
    validation: {}
  });

  // the default values of the attribute, at least an empty object
  const defaultValues = useMemo(() => {
    if (data.defaults == null) return {};
    return { ...data.defaults };
  }, [data]);

  // the validation values of the attribute, at least an empty object
  const validationValues = useMemo(() => {
    if (data.type === 'BOOL' || !data?.validation) return {};
    return { ...data.validation };
  }, [data]);

  // ref that references all translations
  const translationsRef = useRef();
  // if the translations are shown
  const [showTranslations, setShowTranslations] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [showDetailedOptions, setShowDetailedOptions] = useState(false);

  const { openSnackBar } = useSnackbar();
  const navigate = useNavigate();
  const intl = useIntl();
  const api = useApi();
  const { locale } = useLanguage();
  // the current region
  const [region, setRegion] = useState('');
  const [regionArr, setRegionArr] = useState(['DE']);
  // the ref for the current region-specific values
  const regionValuesRef = useRef();
  const allRegions = ['DE', 'CH', 'GB', 'US', 'GLOBAL'];

  /**
   * Loads the data.
   */
  useEffect(() => {
    if (id) {
      api
        .get(`/attribute/${id}?localization=true&lang=${locale}&allAddInfo=true&country=${region}`)
        .then(response => {
          setData(response.data);
          setRegionArr(getNonNullRegions(response.data, allRegions));
          setShowDetailedOptions(response.data?.type !== 'TEXT')
        })
        .catch(error => {
          switch (error.response.status) {
            case 403:
              navigate('/login');
              break;
            default:
              console.log(error);
          }
        });
    }
  }, [id]);

  /**
   * Handle a change in the region.
   * Check current region-specific input-data for validity.
   * @param {*} value 
   * @returns 
   */
  const handleChangeRegion = (value) => {
    // reset region-picker
    setRegion('');
    // add region to region-array
    setRegionArr(old => [...old, value]);
  };

  /**
   * Returns the specified attribute-data in a format necessary for the server.
   * Adds the localization-object.
   * @param {*} data 
   * @returns 
   */
  const getAttributeFormData = (data) => {
    return { ...data, localization: getLanguageFields(data) };
  };

  /**
   * Submits the data.
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    setIsLoading(true);
    const attributeData = getAttributeFormData(data);
    // field 'key' is not needed for server-communication (done through translations)
    delete attributeData.key;
    // check if at least one translation is set for the attribute
    if (!hasLanguageSet(attributeData.localization)) {
      openSnackBar(intl.formatMessage({ id: "EditAttribute.noLanguageSet" }));
      setIsLoading(false);
      return;
    }
    if (id) {
      // update existing attribute
      api
        .put(`/attribute/${id}?lang=${locale}`, attributeData)
        .then((response) => {
          // in case of success, close dialog
          onClose(response.data);
        })
        .catch(error => {
          switch (error.response.status) {
            case 403:
              navigate('/login');
              break;
            case 409:
              openSnackBar(intl.formatMessage({ id: "EditAttribute.changeFields" }));
              break;
            default:
              console.log(error);
              openSnackBar(intl.formatMessage({ id: "EditAttribute.somethingWrong" }));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // create new attribute
      api
        .post(`/attribute?lang=${locale}`, attributeData)
        .then((response) => {
          // in case of success, close dialog
          onClose(response.data);
        })
        .catch(error => {
          switch (error.response.status) {
            case 403:
              navigate('/login');
              break;
            case 409:
              openSnackBar(intl.formatMessage({ id: "EditAttribute.changeFields" }));
              break;
            default:
              console.log(error);
              openSnackBar(intl.formatMessage({ id: "EditAttribute.somethingWrong" }));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  /**
   * Returns an object with key-value-pairs of translations.
   * If showTranslations = true, the specific translations are used.
   * If showTranslations = false, only uses the current locale.
   * @param {*} attributeData 
   * @returns 
   */
  const getLanguageFields = (attributeData) => {
    if (showTranslations) {
      // translationsRef is used
      if (translationsRef.current) {
        return translationsRef.current.getTranslations();
      } else {
        return {};
      }
    } else {
      // translationsRef is not used
      return { ...attributeData.localization };
    }
  };

  /**
   * Sets the language-field of the current locale.
   * data.key and data.key<Language> is updated.
   * @param {*} value 
   */
  const setLocaleField = (value) => {
    setData(old => {
      const o = { ...old };
      o.localization[locale] = value;
      o.key = value;
      return o;
    });
  };

  /**
   * Returns an array of all regions that have a key in data.defaults or data.validation.
   * If there are no non-null-regions, a single default region is supplied ('DE');
   * @param {*} defaultValues 
   * @param {*} validationValues 
   * @param {*} regions 
   * @returns 
   */
  const getNonNullRegions = (data, regions) => {
    const filtered = regions.filter(region => data?.defaults?.[region] || data?.validation?.[region]);
    return filtered.length ? filtered : ['DE'];
  }

  /**
   * Handles a change of the attribute-type.
   * @param {*} value 
   */
  const handleChangeType = (value) => {
    setData(old => {
      // reset default values
      let defaults = {};
      let validation = {};
      if (value === 'BOOL') {
        defaults = null;
        validation = null;
      } else {
        for (const r of REGION_KEYS) {
          defaults[r] = {};
          validation[r] = {};
        }
      }
      return { ...old, defaults, validation, type: value, unitFamily: null };
    });
  };

  /**
   * Handles a change of the unitFamily.
   * Resets all unit-values if no unitFamily is given.
   * @param {*} value 
   */
  const handleChangeUnitFamily = (value) => {
    setData(old => {
      const o = { ...old, unitFamily: value || null };
      // we need to reset all unit-values
      for (const d in o.validation) {
        o.validation[d].unit = null;
      }
      return o;
    });
  };

  /**
   * Handles a change of the linkType.
   * @param {*} value 
   */
  const handleChangeLinkType = (value) => {
    setData(old => {
      const o = { ...old, linkType: value || null };
      return o;
    });
  };

  /**
   * Sets the default-values of the specified country.
   * @param {*} country 
   * @returns 
   */
  const updateDefaults = useCallback((country) => (defaults) => {
    setData(old => {
      const oDefaults = { ...old.defaults }
      oDefaults[country] = defaults;
      return { ...old, defaults: oDefaults };
    });
  }, [setData]);

  /**
   * Sets the validation-values of the specified country.
   * @param {*} country 
   * @returns 
   */
  const updateValidation = useCallback((country) => (validation) => {
    setData(old => {
      const oValidation = { ...old.validation }
      oValidation[country] = validation;
      return { ...old, validation: oValidation };
    });
  }, []);

  /**
   * Sets the default-values..
   * @returns 
   */
  const updateAllDefaults = useCallback((defaults) => {
    setData(old => {
      return { ...old, defaults };
    });
  }, [setData]);

  /**
   * Sets the validation-values.
   * @returns 
   */
  const updateAllValidation = useCallback((validation) => {
    setData(old => {
      return { ...old, validation };
    });
  }, []);

  const attributeRegionValues = useMemo(() => {
    return regionArr.map(region => <AttributeRegionValues ref={regionValuesRef} key={`region-values-${region}`} updateDefaults={updateDefaults(region)} updateValidation={updateValidation(region)} region={region} defaults={defaultValues[region] || {}} validation={validationValues[region] || {}} baseSelection={validationValues['BASE'] || {}} attributeType={data.type} unitFamily={data.unitFamily} />)
  }, [regionArr, defaultValues, validationValues, data, updateDefaults, updateValidation]);

  /**
   * All unit-options that are displayed in a Select-element.
   */
  const unitOptions = useMemo(() => [{ value: '', label: <em><FormattedMessage id='EditAttribute.noDefaultValue' /></em> }].concat(Object.keys(UNIT_CHOICES).map(key => ({ value: key, label: <FormattedMessage id={`EditAttribute.${key.toLowerCase()}`} /> })))
    , [UNIT_CHOICES]);

  const linkTypeOptions = useMemo(() => ['COLOR', 'MATERIAL', 'MODEL', 'PRODUCT', 'MANUFACTURER'].map(type => ({ value: type, label: <FormattedMessage id={`EditAttribute.link_type_${type.toLowerCase()}`} /> })), []);

  const textFieldStyle = {
    width: '90%'
  };

  return (
    <Container maxWidth='xl' >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
        <FormattedMessage id="EditAttribute.heading" />
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid container spacing={3} sx={{ alignItems: 'flex-start', border: '1px dashed black', borderRadius: 3, margin: 0, width: "100%", paddingBottom: 2, alignItems: 'center' }} >
          {
            showTranslations &&
            <TranslationTextFields ref={translationsRef} defaultTranslations={{ ...data.localization }} />
          }
          {
            !showTranslations &&
            <Grid item xs={12} sm={6} md={4}  >
              <TextField
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                name={"key"}
                label={intl.formatMessage({ id: "Attributes.name" })}
                type="text"
                sx={textFieldStyle}
                value={data.key || ''}
                onChange={(event) => setLocaleField(event.target.value)}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          }
          {
            <Grid item xs={12} container alignItems={'center'}>
              <CustomToolTip
                tooltip={showTranslations ? <FormattedMessage id='AttributeBox.hideTranslations' /> : <FormattedMessage id='AttributeBox.showTranslations' />}
                placement={"bottom"}
              >
                <Button
                  onClick={() => setShowTranslations(old => {
                    if (old) {
                      // update localization if switching from multi-translation-text-fields to single field
                      if (translationsRef.current) {
                        const localization = translationsRef.current.getTranslations();
                        setData(data => ({ ...data, key: localization[locale], localization }));
                      }
                    }
                    return !old;
                  })}
                  aria-label='show translations'
                >
                  <TranslateIcon fontSize='large' />
                </Button>
              </CustomToolTip>
            </Grid>
          }
          {
            userInfo?.isAdmin && !showDetailedOptions &&
            <Grid item xs={1} container alignItems={'center'}>
              <CustomToolTip
                tooltip={<FormattedMessage id='AttributeBox.showDetailedOptions' />}
                placement={"bottom"}
              >
                <Button
                  onClick={() => setShowDetailedOptions(true)}
                  aria-label='show detailed options'
                >
                  <AddCircleOutlineIcon fontSize='large' />
                </Button>
              </CustomToolTip>
            </Grid>
          }
          {
            userInfo?.isAdmin && showDetailedOptions && <Grid item xs={12} sm={6} md={4} >
              <FormControl fullWidth>
                <InputLabel id="attribute-type-select-label"><FormattedMessage id="AttributeBox.type" /></InputLabel>
                <Select
                  labelId="attribute-type-select-label"
                  id="attribute-type-select"
                  value={data.type}
                  label={intl.formatMessage({ id: "Attributes.type" })}
                  sx={textFieldStyle}
                  onChange={(event) => handleChangeType(event.target.value)}
                >
                  <MenuItem value={'TEXT'}><FormattedMessage id="AttributeBox.text" /></MenuItem>
                  <MenuItem value={'NUMBER'}><FormattedMessage id="AttributeBox.number" /></MenuItem>
                  <MenuItem value={'BOOL'}><FormattedMessage id="AttributeBox.bool" /></MenuItem>
                  <MenuItem value={'SELECTION'}><FormattedMessage id="AttributeBox.selection" /></MenuItem>
                  <MenuItem value={'LINK'}><FormattedMessage id="AttributeBox.link" /></MenuItem>
                  <MenuItem value={'COORDINATE'}><FormattedMessage id="AttributeBox.coordinate" /></MenuItem>
                  <MenuItem value={'DATE'}><FormattedMessage id="AttributeBox.date" /></MenuItem>
                </Select>
              </FormControl>
            </Grid>
          }
          {
            userInfo?.isAdmin && showDetailedOptions && data.type === 'NUMBER' &&
            <Grid item xs={12} sm={6} md={4} >
              <FormControl fullWidth>
                <InputLabel id="attribute-type-select-label"><FormattedMessage id="EditAttribute.unitFamily" /></InputLabel>
                <Select
                  labelId="attribute-type-select-label"
                  id="attribute-type-select"
                  value={data.unitFamily || ''}
                  label={intl.formatMessage({ id: "EditAttribute.unitFamily" })}
                  sx={textFieldStyle}
                  onChange={(event) => handleChangeUnitFamily(event.target.value)}
                >
                  {
                    unitOptions.map((unit, index) => {
                      return <MenuItem key={index} value={unit.value}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                          alignItems="center"
                        >
                          <Typography>{unit.label}</Typography>
                        </Box>
                      </MenuItem>
                    })
                  }
                </Select>
              </FormControl>
            </Grid>
          }
          {
            userInfo?.isAdmin && showDetailedOptions && data.type === 'LINK' &&
            <Grid item xs={12} sm={6} md={4} >
              <FormControl fullWidth>
                <InputLabel id="attribute-type-select-label"><FormattedMessage id="EditAttribute.linkType" /></InputLabel>
                <Select
                  labelId="attribute-type-select-label"
                  id="attribute-type-select"
                  value={data.linkType || ''}
                  label={intl.formatMessage({ id: "EditAttribute.linkType" })}
                  sx={textFieldStyle}
                  onChange={(event) => handleChangeLinkType(event.target.value)}
                  required="true"
                >
                  {
                    linkTypeOptions.map((type, index) => {
                      return <MenuItem key={index} value={type.value}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                          alignItems="center"
                        >
                          <Typography>{type.label}</Typography>
                        </Box>
                      </MenuItem>
                    })
                  }
                </Select>
              </FormControl>
            </Grid>
          }
          {
            userInfo?.isAdmin && showDetailedOptions && data.type === 'SELECTION' &&
            <Grid item xs={12} >
              <AttributeBaseSelection updateDefaults={updateAllDefaults} updateValidation={updateAllValidation} defaults={defaultValues || {}} validation={validationValues || {}} />
            </Grid>
          }
          {
            userInfo?.isAdmin && showDetailedOptions && data.type !== 'LINK' && data.type !== 'COORDINATE' && data.type !== 'DATE' &&
            <Grid item xs={12}>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center' }} >
                <Typography variant="h6" >
                  <FormattedMessage id="EditAttribute.region" />
                </Typography>
                <RegionPicker region={region} regionsToHide={regionArr} setRegion={handleChangeRegion} />
              </div>
              {attributeRegionValues}
            </Grid>
          }
          {
            userInfo?.isAdmin && !id &&
            [<Grid item key="b1" xs={12}></Grid>,
            <Grid item xs={12} sm={6} md={4} key="g5" >
              <TextField
                required={false}
                sx={textFieldStyle}
                id="id-picker"
                label={intl.formatMessage({ id: "EditAttribute.manualId" })}
                variant="outlined"
                type="number"
                inputProps={{ min: 1, max: 99999, step: 1 }}
                value={data.id || ''}
                onChange={event => setData(old => ({ ...old, id: event.target.value }))}
              >
              </TextField>
            </Grid>]
          }
        </Grid>
        <Button sx={{ marginTop: 2, marginBottom: 2 }} variant="contained" type="submit">
          <FormattedMessage id="Edit.save" />
        </Button>
      </Box>
    </Container>
  );
}